<script>
import TextArea from "../../Forms/TextArea.vue";
import AutoComplete from "../../Forms/AutoComplete.vue";
import DateTimePicker from "../../Master/DateTimePicker.vue";
import TextInput from "../../Forms/TextInput.vue";

export default {
    name: "AccidentReportForm",
    components: { TextInput, DateTimePicker, AutoComplete, TextArea },

    props: {
        value: [Object],
    },

    computed: {
        form: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            },
        },
        types() {
            return this.$page.props.types;
        },
    },

    watch: {
        "form.affectee_type": {
            handler() {
                this.form.affectee_id = null;
            },
        },
        "form.affectee_id": {
            handler() {
                if (this.form.affectee_id === null) {
                    return;
                }

                const model = this.getAffectedModel();

                if (!model) {
                    return;
                }

                this.setAddressFromModel(model);
                this.setNameFromModel(model);
            },
        },
    },

    methods: {
        submit() {
            this.$refs.form.validate().then((success) => {
                if (!success) {
                    this.$toast.error("Please check for errors", {
                        duration: 5000,
                    });
                    return;
                }
                this.$emit("submitForm");
            });
        },
        getAffectedModel() {
            if (this.form.affectee_type === null) {
                return null;
            }

            if (this.form.affectee_type === this.types["user"]) {
                return this.$page.props.users.find(
                    (user) => user.id === this.form.affectee_id
                );
            }

            return this.$page.props.freelancers.find(
                (freelancer) => freelancer.id === this.form.affectee_id
            );
        },

        getAddressFromModel(type, model) {
            if (type === this.types["user"]) {
                return model.personal_details?.home_address;
            }

            return model.address;
        },

        setAddressFromModel(model) {
            const address = this.getAddressFromModel(
                this.form.affectee_type,
                model
            );

            if (!address) {
                return;
            }

            //Filter out the null or empty string values in the address object
            this.form.affectee_address = Object.values(address)
                .filter((value) => value && value !== "" && value !== null)
                .join(", ");
        },

        setNameFromModel(model) {
            this.form.affectee_name = model.name;
        },
    },
};
</script>

<template>
    <v-form @submit.prevent="submit" :disabled="form.processing">
        <validation-observer ref="form">
            <v-row class="align-baseline">
                <v-col>
                    <v-radio-group v-model="form.affected_type" mandatory row>
                        <v-radio label="User" :value="types['user']" />
                        <v-radio
                            label="Freelancer"
                            :value="types['freelancer']"
                        />
                        <v-radio label="Manual" :value="null" />
                    </v-radio-group>
                </v-col>
                <v-col>
                    <auto-complete
                        v-if="form.affected_type === types['user']"
                        :items="$page.props.users"
                        name="User"
                        v-model="form.affected_id"
                        item-text="name"
                        item-value="id"
                        required
                        rules="required"
                    />
                    <auto-complete
                        v-if="form.affected_type === types['freelancer']"
                        :items="$page.props.freelancers"
                        name="Freelancer"
                        v-model="form.affected_id"
                        item-text="name"
                        item-value="id"
                        required
                        rules="required"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <text-input
                        name="Affectee Name"
                        required
                        rules="required"
                        v-model="form.affectee_name"
                    />
                </v-col>
                <v-col cols="6">
                    <text-area
                        name="Affectee Address"
                        required
                        rules="required"
                        v-model="form.affectee_address"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <text-input
                        name="Affectee Occupation"
                        required
                        rules="required"
                        v-model="form.affectee_occupation"
                    />
                </v-col>
                <v-col cols="6">
                    <date-time-picker
                        label="Accident Date"
                        name="Accident Date"
                        v-model="form.accident_date"
                        mode="date"
                        required
                        rules="required"
                        :max="new Date()"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <text-area
                        name="Accident location"
                        required
                        rules="required"
                        v-model="form.accident_location"
                    />
                </v-col>
                <v-col cols="6">
                    <text-area
                        name="Accident description"
                        hint="Describe what happened"
                        required
                        rules="required"
                        v-model="form.accident_description"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <text-area
                        name="Injury description"
                        hint="Describe what the injurys were"
                        required
                        rules="required"
                        v-model="form.injury_description"
                    />
                </v-col>
                <v-col cols="6">
                    <text-area
                        name="Accident cause"
                        required
                        rules="required"
                        v-model="form.accident_cause"
                    />
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="6">
                    <text-area
                        name="Prevention"
                        hint="What could have been done to prevent the accident?"
                        required
                        rules="required"
                        v-model="form.accident_prevention"
                    />
                </v-col>
                <v-col cols="6">
                    <text-area
                        name="Action taken"
                        hint="What action(s) can be taken to prevent this happening in the future?"
                        required
                        rules="required"
                        v-model="form.accident_action"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="6">
                    <text-input
                        name="Job Reference"
                        required
                        rules="required"
                        v-model="form.job_reference"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">
                    <v-btn
                        type="submit"
                        color="success"
                        :loading="form.processing"
                    >
                        {{ form.id ? "Update" : "Create" }}
                    </v-btn>
                </v-col>
            </v-row>
        </validation-observer>
    </v-form>
</template>

<style scoped></style>
